import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ArrowUp, Check, ChevronDown, Mic, PanelLeft, Plus, Search, Share2, SlidersHorizontal, SquarePen } from 'lucide-react';
import { BrandLogo } from './AppChrome';

/** Same mark the site header uses, so Giga reads identically everywhere. */
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png';

/**
 * The conversation, as a sequence of exchanges rather than swappable variants:
 * ChatGPT is a transcript, so the beats append downward and the thread scrolls
 * to follow — which is what makes it read as the real surface rather than a
 * card that re-renders in place.
 */
type Beat = {
  /** Text after the @Giga mention, which is rendered as a highlighted chip. */
  prompt: string;
  /** The app-call disclosure ChatGPT shows above an answer that used a tool. */
  reading: string;
  sources: string[];
  detail: string;
  lead: string;
  body: React.ReactNode;
};
const AD_SETS = [{
  name: 'Ad set 1 · Broad',
  roas: '4.2×',
  cpa: '$18',
  good: true
}, {
  name: 'Ad set 2 · Lookalike',
  roas: '1.1×',
  cpa: '$72',
  good: false
}, {
  name: 'Ad set 3 · Interests',
  roas: '0.9×',
  cpa: '$88',
  good: false
}, {
  name: 'Ad set 4 · Retargeting',
  roas: '3.8×',
  cpa: '$21',
  good: true
}];
function AdSetTable() {
  return <div className="overflow-hidden rounded-lg border border-[#E5E5E5] text-[13px]" data-id="element-307" data-mp="3o4g48rurk6we">
      <div className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-[#E5E5E5] bg-[#F9F9F9] text-[11px] font-semibold text-[#5D5D5D]" data-id="element-308" data-mp="3j30l55c5cagn">
        <span className="px-3 py-2" data-id="element-309" data-mp="3t1ps432i8ai4">Ad set</span>
        <span className="px-3 py-2 text-right" data-id="element-310" data-mp="3t1zhbofq6dmr">ROAS</span>
        <span className="px-3 py-2 text-right" data-id="element-311" data-mp="23m04nxcwe62d">CPA</span>
      </div>
      {AD_SETS.map(r => <div key={r.name} className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-[#F0F0F0] last:border-0" data-id="element-312" data-mp="1ynosvh31llhi">
          <span className="truncate px-3 py-2 text-[#0D0D0D]" data-id="element-313" data-mp="2oc2ki49hc35f">{r.name}</span>
          <span className={`px-3 py-2 text-right ${r.good ? 'text-[#0D0D0D]' : 'font-semibold text-[#C2410C]'}`} data-id="element-314" data-mp="2sd632yiwwdac">
            {r.roas}
          </span>
          <span className="px-3 py-2 text-right text-[#5D5D5D]" data-id="element-315" data-mp="2xbh8mx26c1mr">{r.cpa}</span>
        </div>)}
    </div>;
}
const BEATS: Beat[] = [{
  prompt: 'how did our Meta ads do last week?',
  reading: 'Reading Meta Ads',
  sources: ['meta'],
  detail: '4 ad sets · last 7 days',
  lead: 'Here is last week across your four active ad sets.',
  body: <div className="flex flex-col gap-3" data-id="element-316" data-mp="3b9nq52mtfp09">
      <AdSetTable data-id="element-317" data-mp="3g2gvnsq3z1de" />
      <p data-id="element-318" data-mp="3g41ondwryc95">Ad sets 2 and 3 are well under your 2× ROAS target — worth a closer look.</p>
    </div>
}, {
  prompt: 'which should we cut, and where should the budget go?',
  reading: 'Reading company brain',
  sources: ['giga'],
  detail: 'growth/targets.md · 2 observations',
  lead: 'Weighed against the targets in your growth track:',
  body: <ul className="flex list-disc flex-col gap-1 pl-5" data-id="element-319" data-mp="3uxn1aelaky67">
      <li data-id="element-320" data-mp="1jdfhqugeyy4f">
        Pause <strong className="font-semibold" data-id="element-321" data-mp="1jdz0gddw1qji">ad set 2</strong> — 1.1× ROAS, $72 CPA
      </li>
      <li data-id="element-322" data-mp="1ebzvx06ewnkz">
        Pause <strong className="font-semibold" data-id="element-323" data-mp="1efnqm2nlfg1a">ad set 3</strong> — 0.9× ROAS, $88 CPA
      </li>
      <li data-id="element-324" data-mp="19eqadvm58w2f">Move that budget to ad sets 1 &amp; 4, your top performers</li>
    </ul>
}, {
  prompt: 'go ahead — and let the team know in Slack.',
  reading: 'Writing to Meta Ads, Slack',
  sources: ['meta', 'slack'],
  detail: '4 changes applied · posted to #growth',
  lead: 'Done. Changes are live in Meta Ads Manager:',
  body: <div className="flex flex-col gap-3" data-id="element-325" data-mp="1x6ewuvcaaqbg">
      <ul className="flex flex-col gap-1.5" data-id="element-326" data-mp="1sckim7fg73jr">
        {['Paused ad set 2', 'Paused ad set 3', 'Ad set 1 budget +40%', 'Ad set 4 budget +40%'].map(t => <li key={t} className="flex items-center gap-2" data-id="element-327" data-mp="1nc6irco02zwt">
            <Check size={14} strokeWidth={2.6} className="shrink-0 text-[#059669]" data-id="element-328" data-mp="2kuoncakg028x" />
            {t}
          </li>)}
      </ul>
      <blockquote className="border-l-2 border-[#E5E5E5] pl-3 text-[#5D5D5D]" data-id="element-329" data-mp="2g1lslz3ximr5">
        Heads up team — pausing ad sets 2 &amp; 3 (ROAS under 1×) and moving budget to 1 &amp; 4.
      </blockquote>
    </div>
}];

/**
 * The app-call disclosure ChatGPT renders when a connected app answered: the
 * app's mark and name, what it did, and the specific sources it touched — the
 * proof that the answer came from the company's own systems rather than the
 * model. It shows the live action first, then settles to the result.
 */
function GigaAppCall({
  reading,
  sources,
  detail,
  working
}: {
  reading: string;
  sources: string[];
  detail: string;
  working: boolean;
}) {
  // ChatGPT renders a tool call as a quiet inline line above the answer, not as
  // a bordered card — the card was the main thing making this read as a generic
  // panel rather than ChatGPT.
  return <div className="flex flex-col gap-1.5" data-id="element-330" data-mp="3297q8v8khjtt">
      <div className="flex items-center gap-1.5" data-id="element-331" data-mp="328fu9jb25k30">
        <GigaTile className="size-[15px] rounded-[4px]" markClass="size-[9px]" data-id="element-332" data-mp="3720uzj41cs5w" />
        <span className={`truncate text-[13px] ${working ? 'text-[#5D5D5D]' : 'text-[#8F8F8F]'}`} data-id="element-333" data-mp="vlgshynf1m1y">
          {working ? `${reading}…` : detail}
        </span>
        <ChevronDown size={13} className="shrink-0 text-[#B4B4B4]" data-id="element-334" data-mp="qkt47kqknjbf" />
      </div>
      {!working && <div className="flex flex-wrap items-center gap-1.5 pl-[21px]" data-id="element-335" data-mp="15b0hlm1q2c59">
          {sources.map(s => <span key={s} className="inline-flex items-center gap-1.5 rounded-md bg-[#F4F4F4] px-1.5 py-[3px]" data-id="element-336" data-mp="10dieicadyr56">
              <BrandLogo slug={s} className="size-[11px]" data-id="element-337" data-mp="cp8mutqhr964" />
              <span className="text-[11px] font-medium text-[#5D5D5D]" data-id="element-338" data-mp="7p311u6wkgvy">
                {SOURCE_NAMES[s] ?? s}
              </span>
            </span>)}
        </div>}
    </div>;
}

/**
 * The mention is what makes this surface legible: the user is not talking to
 * ChatGPT about their company, they are addressing Giga inside ChatGPT. It gets
 * the same highlighted-chip treatment ChatGPT gives a resolved mention.
 */
function GigaMention() {
  // Mark plus orange text, no chip fill — the fill was a third background
  // inside an already-tinted bubble.
  return <span className="mr-1 inline-flex items-center gap-1.5 align-middle font-semibold text-[#F97316]" data-id="element-339" data-mp="upl2hpmk9gnk">
      <GigaTile className="size-[16px] rounded-[5px]" markClass="size-[9px]" data-id="element-340" data-mp="zoxq4cl9arr7" />
      @Giga
    </span>;
}

/** Giga's mark as the brand lockup: white on the orange tile. */
function GigaTile({
  className,
  markClass
}: {
  className: string;
  markClass: string;
}) {
  // The brighter brand orange, not the deep #EA580C used for text on cream —
  // as a solid fill that darker value reads distinctly red.
  return <span className={`flex shrink-0 items-center justify-center bg-[#F97316] ${className}`} data-id="element-341" data-mp="g05h6p5su3f9">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className={`${markClass} brightness-0 invert`} data-id="element-342" data-mp="kr5f9xqnmrjc" />
    </span>;
}
const SOURCE_NAMES: Record<string, string> = {
  giga: 'Company brain',
  meta: 'Meta Ads',
  slack: 'Slack',
  salesforce: 'Salesforce',
  notion: 'Notion',
  gmail: 'Gmail',
  intercom: 'Intercom'
};
const SIDEBAR_CHATS = ['Meta ads — last week', 'Q3 pipeline review', 'Renewal risk summary', 'Landing page copy'];

/**
 * ChatGPT's own chrome, rebuilt: collapsible sidebar with recents, the model
 * name as a dropdown in the header, an unbubbled assistant response, and the
 * pill composer. Previously this screen was a bare card holding two bubbles,
 * which is why it did not read as ChatGPT — the bubbles were right, but none of
 * the surrounding surface that identifies the product was there.
 */
export function ChatGPTPanel({
  className = '',
  heightClass = 'h-[560px]',
  settled = false
}: {
  className?: string;
  heightClass?: string;
  /**
   * Render the completed transcript instead of playing it. The export page
   * needs the finished thread, not whichever beat the timer was on.
   */
  settled?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    amount: 0.35
  });
  // How many exchanges have landed, and whether the newest is still working.
  const [shown, setShown] = useState(settled ? BEATS.length : 1);
  const [working, setWorking] = useState(false);
  useEffect(() => {
    if (!inView || settled) return;
    setShown(1);
    setWorking(true);
  }, [inView, settled]);
  useEffect(() => {
    if (!inView || settled) return;
    if (working) {
      const id = setTimeout(() => setWorking(false), 1500);
      return () => clearTimeout(id);
    }
    if (shown >= BEATS.length) return;
    const id = setTimeout(() => {
      setShown(s => s + 1);
      setWorking(true);
    }, 2600);
    return () => clearTimeout(id);
  }, [inView, working, shown, settled]);
  // Follow the transcript as it grows, the way the real thread does. Settled
  // jumps rather than scrolls, so the frame is never captured mid-glide.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTo({
      top: el.scrollHeight,
      behavior: settled ? 'auto' : 'smooth'
    });
  }, [shown, working, settled]);
  return <div ref={ref} className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white text-left shadow-[0_1px_2px_rgba(43,33,26,0.05),0_8px_24px_-12px_rgba(43,33,26,0.18),0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-343" data-mp="2huzhvvo69ir2">
      <div className={`flex ${heightClass}`} data-id="element-344" data-mp="2mrzp49wrcrjr">
        {/* Sidebar */}
        <aside className="hidden w-[212px] shrink-0 flex-col bg-[#F9F9F9] px-2 py-3 sm:flex" data-id="element-345" data-mp="30s0otm1adsxn">
          <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-346" data-mp="2vsxjs1lb3mpo">
            <BrandLogo slug="chatgpt" className="size-[18px]" data-id="element-347" data-mp="2vsnukg835jl1" />
            <PanelLeft size={15} className="text-[#B4B4B4]" data-id="element-348" data-mp="3agscvj387t3a" />
          </div>
          {[{
          icon: SquarePen,
          label: 'New chat'
        }, {
          icon: Search,
          label: 'Search chats'
        }].map(item => <div key={item.label} className="flex items-center gap-2.5 rounded-lg px-2 py-[7px]" data-id="element-349" data-mp="3jirvgftw358c">
              <item.icon size={15} strokeWidth={1.9} className="text-[#5D5D5D]" data-id="element-350" data-mp="3firi6yij102v" />
              <span className="text-[13.5px] text-[#0D0D0D]" data-id="element-351" data-mp="3hjhmelmni59p">{item.label}</span>
            </div>)}
          <p className="mb-1 mt-4 px-2 text-[12px] font-medium text-[#8F8F8F]" data-id="element-352" data-mp="3cr7mqdvqhqu8">Chats</p>
          <ul className="flex flex-col" data-id="element-353" data-mp="37ty1lafdo1c7">
            {SIDEBAR_CHATS.map((c, i) => <li key={c} className={`truncate rounded-lg px-2 py-[6px] text-[13px] ${i === 0 ? 'bg-[#EDEDED] font-medium text-[#0D0D0D]' : 'text-[#5D5D5D]'}`} data-id="element-354" data-mp="32u39x4ns37q3" data-mpi={i}>
                {c}
              </li>)}
          </ul>
          <div className="mt-auto flex items-center gap-2.5 rounded-lg px-2 py-1.5" data-id="element-355" data-mp="3vhz7ebl693nx">
            <span className="flex size-[22px] items-center justify-center rounded-full bg-[#0D8B6C] text-[10px] font-semibold text-white" data-id="element-356" data-mp="3qj4dadhr7soi">
              SC
            </span>
            <span className="truncate text-[13px] text-[#0D0D0D]" data-id="element-357" data-mp="3mk5aayfl25zp">Sarah Chen</span>
          </div>
        </aside>

        {/* Conversation */}
        <div className="flex min-w-0 flex-1 flex-col bg-white" data-id="element-358" data-mp="2b2dxpo539fup">
          <header className="flex shrink-0 items-center justify-between px-4 py-3" data-id="element-359" data-mp="qkl9mouirxzc">
            <span className="inline-flex items-center gap-1 text-[15px] font-medium text-[#0D0D0D]" data-id="element-360" data-mp="qiibtin4l0k2">
              ChatGPT
              <span className="text-[#8F8F8F]" data-id="element-361" data-mp="zljflcoiqfkk">5</span>
              <ChevronDown size={15} className="text-[#8F8F8F]" data-id="element-362" data-mp="zjqa40pn61e5" />
            </span>
            <span className="flex items-center gap-3 text-[#5D5D5D]" data-id="element-363" data-mp="14crqkd5hy8zu">
              <Share2 size={16} strokeWidth={1.9} data-id="element-364" data-mp="19bm7tantfphu" />
              <SlidersHorizontal size={16} strokeWidth={1.9} data-id="element-365" data-mp="19ata0z6u4abu" />
            </span>
          </header>

          <div ref={threadRef} className="flex-1 overflow-hidden px-4 pb-2 sm:px-6" data-id="element-366" data-mp="1e8co9z3j8n3s">
            <div className="mx-auto flex max-w-[620px] flex-col gap-6 py-2 text-[15px] leading-[1.65] text-[#0D0D0D]" data-id="element-367" data-mp="1icajvztnffs3">
              {BEATS.slice(0, shown).map((beat, i) => {
              const isLatest = i === shown - 1;
              const pending = isLatest && working;
              return <div key={beat.prompt} className="flex flex-col gap-4" data-id="element-368" data-mp="3p3a4r62txi0n" data-mpi={i}>
                    <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="max-w-[85%] self-end rounded-[22px] bg-[#F4F4F4] px-4 py-2.5" data-id="element-369" data-mp="w78job8dfg51">
                      <GigaMention data-id="element-370" data-mp="1f6mdmt63oqvj" />
                      {beat.prompt}
                    </motion.div>
                    <div className="flex flex-col gap-3" data-id="element-371" data-mp="1a7rm92u2htzt">
                      <GigaAppCall reading={beat.reading} sources={beat.sources} detail={beat.detail} working={pending} data-id="element-372" data-mp="1a79k5pkq4ljz" />
                      <AnimatePresence mode="wait" data-id="element-373" data-mp="1o0r1r4tuno7d">
                        {pending ? <motion.div key="working" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.15
                    }} className="flex items-center gap-1.5 py-1" data-id="element-374" data-mp="1jxurc1egqyei">
                            {[0, 1, 2].map(d => <motion.span key={d} className="size-[6px] rounded-full bg-[#B4B4B4]" animate={{
                        opacity: [0.3, 1, 0.3]
                      }} transition={{
                        duration: 1.1,
                        repeat: Infinity,
                        delay: d * 0.16,
                        ease: 'linear'
                      }} data-id="element-375" data-mp="2hhkvto29rbou" />)}
                          </motion.div> : <motion.div key="answer" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      duration: 0.22
                    }} className="flex flex-col gap-3" data-id="element-376" data-mp="1yktgfppulgq2">
                            <p data-id="element-377" data-mp="1avibdw1gt064">{beat.lead}</p>
                            {beat.body}
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Composer */}
          <div className="shrink-0 px-4 pb-4 sm:px-6" data-id="element-378" data-mp="12tholpzft6kp">
            <div className="mx-auto flex max-w-[620px] items-center gap-2 rounded-[26px] border border-[#E5E5E5] bg-white px-3 py-2.5 shadow-[0_2px_8px_rgba(13,13,13,0.05)]" data-id="element-379" data-mp="12vuecpbfsl8b">
              <Plus size={18} strokeWidth={1.9} className="shrink-0 text-[#5D5D5D]" data-id="element-380" data-mp="t3ewrt41xyny" />
              <span className="min-w-0 flex-1 truncate text-[15px] text-[#8F8F8F]" data-id="element-381" data-mp="p34hfpuddvwf">Ask anything</span>
              <Mic size={17} strokeWidth={1.9} className="shrink-0 text-[#5D5D5D]" data-id="element-382" data-mp="k2gtjd1ftv6f" />
              <span className="flex size-[28px] shrink-0 items-center justify-center rounded-full bg-[#0D0D0D]" data-id="element-383" data-mp="f587v2h2z90f">
                <ArrowUp size={15} strokeWidth={2.4} className="text-white" data-id="element-384" data-mp="a6dpg1n0zmgu" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}